// Unified geolocation helper.
// Defensive by design so iOS Safari / iOS Chrome (which is WebKit) can't
// hang or crash the app when GPS is denied or the Capacitor bridge is
// unavailable.
//
// * On native (Capacitor Android/iOS APK)  → @capacitor/geolocation
// * On any browser (Safari, Chrome, PWA)   → navigator.geolocation
//
// Both plugins are loaded LAZILY inside a try/catch — if their JS chunks
// throw for any reason on a specific device, the web fallback still works
// and the app does not go blank.

// ---------------------------------------------------------------------------
// Lazy, crash-proof Capacitor probe
// ---------------------------------------------------------------------------
let _capacitorProbePromise = null;

function probeCapacitor() {
  if (_capacitorProbePromise) return _capacitorProbePromise;
  _capacitorProbePromise = (async () => {
    // The Capacitor bridge injects `window.Capacitor` inside the WKWebView.
    // If it isn't there, we are 100% in a plain browser — no need to touch
    // the plugins at all.
    if (typeof window === "undefined" || !window.Capacitor) {
      return { isNative: false, Geolocation: null };
    }
    try {
      const [{ Capacitor }, { Geolocation }] = await Promise.all([
        import("@capacitor/core"),
        import("@capacitor/geolocation"),
      ]);
      const isNative = !!(Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform());
      return { isNative, Geolocation };
    } catch (_e) {
      // Any failure loading Capacitor → treat as web.
      return { isNative: false, Geolocation: null };
    }
  })();
  return _capacitorProbePromise;
}

// iOS Safari behaviour: the `timeout` option is IGNORED while the permission
// prompt is visible, and callbacks silently never fire if the user has
// previously denied location for this site. Therefore:
//   1. Ask low-accuracy first (fast — 1-3s over Wi-Fi + cell towers).
//   2. Accept up to a 30s-old cached position so a second tap is instant.
//   3. Wrap the whole thing in a hard `Promise.race` deadline so nothing can
//      keep the UI spinning forever.
const DEFAULT_OPTS = {
  enableHighAccuracy: false,
  timeout: 10000,
  maximumAge: 30000,
};

// Absolute ceiling per browser attempt, independent of the `timeout` option.
const HARD_DEADLINE_MS = 15000;

export async function isNative() {
  const p = await probeCapacitor();
  return p.isNative;
}

export async function ensureLocationPermission() {
  const p = await probeCapacitor();
  if (!p.isNative || !p.Geolocation) return true;
  try {
    const status = await p.Geolocation.checkPermissions();
    if (status.location === "granted") return true;
    const req = await p.Geolocation.requestPermissions();
    if (req.location === "granted") return true;
  } catch (_e) {
    // Permission check itself failed — fall through and let the actual
    // getCurrentPosition call surface the real error.
    return true;
  }
  throw new Error("Location permission denied");
}

function mapGeoError(e) {
  if (!e) return new Error("Failed to get location");
  // PositionError codes: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
  if (e.code === 1) {
    return new Error(
      "Location access is blocked. On iPhone: Settings → Privacy & Security → Location Services → Safari Websites → While Using, then reload this page."
    );
  }
  if (e.code === 2) {
    return new Error(
      "Your device could not get a GPS fix. Try moving near a window, or turn Wi-Fi back on and retry."
    );
  }
  if (e.code === 3) {
    return new Error("Location request timed out. Please try again.");
  }
  return new Error(e.message || "Failed to get location");
}

function browserGetPosition(opts) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return reject(new Error("GPS not supported on this device"));
    }
    let settled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          source: "web",
        });
      },
      (e) => {
        if (settled) return;
        settled = true;
        reject(mapGeoError(e));
      },
      opts
    );
  });
}

function withDeadline(promise, ms, message) {
  let t;
  const deadline = new Promise((_, reject) => {
    t = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(t));
}

export async function getCurrentPosition(options = {}) {
  const opts = { ...DEFAULT_OPTS, ...options };
  const probe = await probeCapacitor();

  // Native (Capacitor) path
  if (probe.isNative && probe.Geolocation) {
    try {
      await ensureLocationPermission();
      const pos = await probe.Geolocation.getCurrentPosition(opts);
      return {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        source: "native",
      };
    } catch (e) {
      // Fall through to browser API — belt-and-braces on Android WebView oddities.
      // eslint-disable-next-line no-console
      console.warn("[geolocation] Capacitor call failed, falling back to browser API:", e?.message || e);
    }
  }

  // Web path — every attempt wrapped in a fresh hard deadline.
  const denialHint =
    "Location is taking too long. Please tap Allow when your browser asks for location. On iPhone, also check Settings → Privacy → Location Services → Safari / Chrome → While Using.";
  try {
    return await withDeadline(browserGetPosition(opts), HARD_DEADLINE_MS, denialHint);
  } catch (firstErr) {
    const msg = firstErr && firstErr.message ? String(firstErr.message) : "";
    // If low-accuracy said "no fix", try once with GPS chips enabled.
    if (msg.startsWith("Your device could not get a GPS fix")) {
      const hi = { ...opts, enableHighAccuracy: true, timeout: 15000 };
      return withDeadline(browserGetPosition(hi), HARD_DEADLINE_MS, denialHint);
    }
    throw firstErr;
  }
}
